import { QrCodeFieldConfig, QrVcardFieldKey } from 'editor/src/store/design/types';

import { emptyContentFor } from './defaults';
import resolveQrFieldSource, { GetColumnValue, GetLinkedText } from './resolveQrFieldSource';
import { QrContentData, QrContentType, QrWifiEncryption } from './types';
import { buildVcardFromConfig, QR_VCARD_FIELD_KEYS, QR_VCARD_FIELD_LABELS } from './vcardFields';

/** Tab order for the QR content type picker in the seller configuration dialog. */
export const QR_TYPE_ORDER: QrContentType[] = ['url', 'vcard', 'email', 'phone', 'sms', 'wifi', 'text'];

export const QR_TYPE_LABELS: Record<QrContentType, string> = {
  url: 'Website',
  vcard: 'Contact card',
  email: 'Email',
  phone: 'Phone',
  sms: 'SMS',
  wifi: 'WiFi',
  text: 'Text',
};

export interface QrFieldDef {
  key: string;
  label: string;
  multiline?: boolean;
  options?: { value: string; label: string }[];
}

/** Per-content-type field definitions driving the seller field-mapping rows. */
export const QR_TYPE_FIELDS: Record<QrContentType, QrFieldDef[]> = {
  url: [{ key: 'url', label: 'Website URL' }],
  text: [{ key: 'text', label: 'Text', multiline: true }],
  email: [
    { key: 'to', label: 'Email address' },
    { key: 'subject', label: 'Subject' },
    { key: 'body', label: 'Message', multiline: true },
  ],
  phone: [{ key: 'number', label: 'Phone number' }],
  sms: [
    { key: 'number', label: 'Phone number' },
    { key: 'message', label: 'Message', multiline: true },
  ],
  wifi: [
    { key: 'ssid', label: 'Network name (SSID)' },
    { key: 'password', label: 'Password' },
    {
      key: 'encryption',
      label: 'Encryption',
      options: [
        { value: 'WPA', label: 'WPA/WPA2' },
        { value: 'WEP', label: 'WEP' },
        { value: 'nopass', label: 'No password' },
      ],
    },
    {
      key: 'hidden',
      label: 'Hidden network',
      options: [
        { value: '', label: 'No' },
        { value: 'true', label: 'Yes' },
      ],
    },
  ],
  vcard: QR_VCARD_FIELD_KEYS.map((key: QrVcardFieldKey) => ({ key, label: QR_VCARD_FIELD_LABELS[key] })),
};

/**
 * Resolves the seller-configured field mapping for the active content type into the
 * QrContentData consumed by buildQrPayload. Element-linked fields read the current text
 * of the linked personalizable element via `getLinkedText`; static fields use their fixed
 * value; column fields read the code set through `getColumnValue`.
 * Unmapped/empty fields are left at their blank default (start from emptyContentFor so
 * every field the type supports is present in the returned shape).
 */
export function buildQrContentFromConfig(
  type: QrContentType,
  fields: QrCodeFieldConfig[],
  getLinkedText: GetLinkedText,
  getColumnValue?: GetColumnValue,
): QrContentData {
  if (type === 'vcard') {
    return buildVcardFromConfig(fields, getLinkedText, getColumnValue);
  }

  const content = emptyContentFor(type) as unknown as Record<string, unknown>;
  const allowedKeys = QR_TYPE_FIELDS[type].map((def) => def.key);

  fields.forEach((field) => {
    // Only registered keys for the active type: a stray key (stale config after a
    // type switch, hand-authored design JSON) must not clobber `type` or other props.
    if (!allowedKeys.includes(field.key)) {
      return;
    }
    const value = resolveQrFieldSource(field.source, getLinkedText, getColumnValue)?.trim();
    if (!value) {
      return;
    }
    if (type === 'wifi' && field.key === 'encryption') {
      content.encryption = value as QrWifiEncryption;
    } else if (type === 'wifi' && field.key === 'hidden') {
      content.hidden = value === 'true';
    } else {
      content[field.key] = value;
    }
  });

  return content as unknown as QrContentData;
}
